export default class PageAnchorObserver {

    constructor() {

        this.settings = {
            menuSelector: '.page__anchor-menu',
            linkSelector: '.page__anchor-menu a',
            activeClass: 'is-active',
            observerOptions: {
                root: null,
                // Track sections as they pass through a narrow band near the viewport center.
                rootMargin: '-45% 0px -45% 0px',
                threshold: 0,
            },
        };

        this.linkMap = new Map();
        this.items = [];
        this.observer = null;
        this.isTicking = false;
        this.activeLink = null;
        this.activeListItem = null;

    }

    init() {

        const menu = document.querySelector( this.settings.menuSelector );

        if ( ! menu ) {
            return;
        }

        const links = Array.from( document.querySelectorAll( this.settings.linkSelector ) );

        if ( links.length === 0 ) {
            return;
        }

        links.forEach( ( link ) => {
            const hash = this.getHashFromLink( link );

            if ( ! hash || hash.length < 2 ) {
                return;
            }

            const section = this.getSectionFromHash( hash );

            if ( ! section ) {
                return;
            }

            this.linkMap.set( section, link );
            this.items.push( { link, section } );
        } );

        if ( this.linkMap.size === 0 ) {
            return;
        }

        this.resetAllActiveStates();

        this.bindScrollSync();

        if ( typeof window.IntersectionObserver !== 'undefined' ) {
            this.observer = new IntersectionObserver(
                ( entries ) => this.onIntersect( entries ),
                this.settings.observerOptions
            );

            this.linkMap.forEach( ( _, section ) => {
                this.observer.observe( section );
            } );
        }

        this.syncActiveLink();

    }

    onIntersect( entries ) {

        if ( entries.length === 0 ) {
            return;
        }

        this.queueSync();

    }

    resetAllActiveStates() {

        const allLinks = document.querySelectorAll( this.settings.linkSelector );

        allLinks.forEach( ( link ) => {
            link.parentElement.classList.remove( this.settings.activeClass );

            if ( link.parentElement ) {
                link.parentElement.classList.remove( this.settings.activeClass );
            }
        } );

        this.activeLink = null;
        this.activeListItem = null;

    }

    updateActiveState( activeItem ) {

        const nextLink = activeItem ? activeItem.link : null;
        const nextListItem = nextLink && nextLink.parentElement ? nextLink.parentElement : null;

        if ( this.activeLink === nextLink ) {
            return;
        }

        if ( this.activeLink ) {
            this.activeLink.parentElement.classList.remove( this.settings.activeClass );
        }

        if ( this.activeListItem ) {
            this.activeListItem.parentElement.classList.remove( this.settings.activeClass );
        }

        if ( nextLink ) {
            nextLink.parentElement.classList.add( this.settings.activeClass );
        }

        if ( nextListItem ) {
            nextListItem.parentElement.classList.add( this.settings.activeClass );
        }

        this.activeLink = nextLink;
        this.activeListItem = nextListItem;

    }

    getHashFromLink( link ) {

        const href = link.getAttribute( 'href' );

        if ( ! href ) {
            return '';
        }

        if ( href.startsWith( '#' ) ) {
            return href;
        }

        if ( href.startsWith( 'http://' ) || href.startsWith( 'https://' ) || href.startsWith( '/' ) ) {
            try {
                const url = new URL( href, window.location.href );
                return url.hash || '';
            } catch ( error ) {
                return '';
            }
        }

        return '#' + href.replace( /^#/, '' );

    }

    getSectionFromHash( hash ) {

        try {
            const section = document.querySelector( hash );

            if ( section ) {
                return section;
            }
        } catch ( error ) {
            // Ignore invalid selector strings and fallback to id lookup.
        }

        return document.getElementById( hash.replace( /^#/, '' ) );

    }

    bindScrollSync() {

        window.addEventListener( 'scroll', () => this.queueSync(), { passive: true } );
        window.addEventListener( 'resize', () => this.queueSync() );

    }

    queueSync() {

        if ( this.isTicking ) {
            return;
        }

        this.isTicking = true;

        window.requestAnimationFrame( () => {
            this.syncActiveLink();
            this.isTicking = false;
        } );

    }

    syncActiveLink() {

        if ( this.items.length === 0 ) {
            return;
        }

        const activationPoint = window.innerHeight * 0.35;
        let activeItem = null;

        this.items.forEach( ( item ) => {
            const rect = item.section.getBoundingClientRect();

            if ( rect.top <= activationPoint && rect.bottom > activationPoint ) {
                activeItem = item;
            }
        } );

        if ( ! activeItem ) {
            for ( let i = this.items.length - 1; i >= 0; i-- ) {
                const rect = this.items[i].section.getBoundingClientRect();

                if ( rect.top <= activationPoint ) {
                    activeItem = this.items[i];
                    break;
                }
            }
        }

        this.updateActiveState( activeItem );

    }

}
